import { Link } from "react-router-dom";
import { useEffect, useRef } from "react";
import logo from "@/assets/logo.webp";
import { Button } from "@/components/ui/button";
import { MapPin, Phone, Mail, Clock, Instagram, Linkedin, Youtube, ExternalLink, MessageCircle, ShieldCheck } from "lucide-react";

const Footer = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    let animationId: number;
    let width = 0;
    let height = 0;
    const dpr = window.devicePixelRatio || 1;

    type Node = {
      x: number;
      y: number;
      vx: number;
      vy: number;
      r: number;
      phase: number;
      speed: number;
    };
    let nodes: Node[] = [];

    const resize = () => {
      const rect = canvas.getBoundingClientRect();
      width = rect.width;
      height = rect.height;
      canvas.width = width * dpr;
      canvas.height = height * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

      const count = Math.max(18, Math.floor(width / 35));
      nodes = Array.from({ length: count }, () => ({
        x: Math.random() * width,
        y: Math.random() * height,
        vx: (Math.random() - 0.5) * 0.25,
        vy: (Math.random() - 0.5) * 0.25,
        r: 1.2 + Math.random() * 1.8,
        phase: Math.random() * Math.PI * 2,
        speed: 0.012 + Math.random() * 0.02,
      }));
    };

    resize();
    window.addEventListener("resize", resize);

    // Brand colors (HSL from design system)
    const primary = "213, 55%, 55%"; // brand blue
    const accent = "175, 50%, 55%"; // teal accent

    const render = () => {
      ctx.clearRect(0, 0, width, height);

      // Update
      for (const n of nodes) {
        n.x += n.vx;
        n.y += n.vy;
        n.phase += n.speed;
        if (n.x < 0 || n.x > width) n.vx *= -1;
        if (n.y < 0 || n.y > height) n.vy *= -1;
      }

      // Connections
      const maxDist = 120;
      for (let i = 0; i < nodes.length; i++) {
        for (let j = i + 1; j < nodes.length; j++) {
          const a = nodes[i];
          const b = nodes[j];
          const dx = a.x - b.x;
          const dy = a.y - b.y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < maxDist) {
            const alpha = (1 - dist / maxDist) * 0.35;
            ctx.strokeStyle = `hsla(${primary}, ${alpha})`;
            ctx.lineWidth = 0.6;
            ctx.beginPath();
            ctx.moveTo(a.x, a.y);
            ctx.lineTo(b.x, b.y);
            ctx.stroke();
          }
        }
      }

      // Nodes (pulsing)
      for (const n of nodes) {
        const pulse = 0.55 + Math.sin(n.phase) * 0.45;
        const radius = n.r * (1 + pulse * 0.6);
        const color = Math.random() > 0.5 ? primary : accent;

        // Glow
        const glow = ctx.createRadialGradient(n.x, n.y, 0, n.x, n.y, radius * 5);
        glow.addColorStop(0, `hsla(${primary}, ${0.5 * pulse})`);
        glow.addColorStop(1, `hsla(${primary}, 0)`);
        ctx.fillStyle = glow;
        ctx.beginPath();
        ctx.arc(n.x, n.y, radius * 5, 0, Math.PI * 2);
        ctx.fill();

        // Core
        ctx.fillStyle = `hsla(${color}, ${0.6 + pulse * 0.4})`;
        ctx.beginPath();
        ctx.arc(n.x, n.y, radius, 0, Math.PI * 2);
        ctx.fill();
      }

      animationId = requestAnimationFrame(render);
    };
    render();

    return () => {
      cancelAnimationFrame(animationId);
      window.removeEventListener("resize", resize);
    };
  }, []);

  return (
    <footer className="bg-foreground text-primary-foreground dark:[--foreground:218_30%_7%] dark:[--primary-foreground:210_20%_90%]">
      {/* Main Footer */}
      <div className="py-12">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-8 lg:gap-12">
            {/* Column 1 - Company Info */}
            <div className="space-y-6">
              <div className="flex items-center">
                <img src={logo} alt="Equilybra Company" width={126} height={80} className="h-20 w-auto object-contain" />
              </div>
              
              <p className="text-primary-foreground/80 text-sm leading-relaxed">
                Clínica Equilybra Company: Compromisso com a sua saúde mental e bem-estar.
              </p>

              <div className="space-y-3">
                <div className="flex items-start gap-3 text-sm">
                  <MapPin className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                  <span className="text-primary-foreground/80">
                    Rua Agostinho Gomes, 3319 – Ipiranga, São Paulo – SP<br />
                    Próximo ao metrô Sacomã
                  </span>
                </div>
                <div className="flex items-start gap-3 text-sm">
                  <Phone className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                  <div className="text-primary-foreground/80 space-y-1">
                    <div className="flex flex-wrap gap-x-3">
                      <a href="tel:1123050747" className="hover:text-primary transition-colors">(11) 2305-0747</a>
                    </div>
                    <a href="tel:11976974905" className="hover:text-primary transition-colors block">(11) 97697-4905 (somente mensagem)</a>
                  </div>
                </div>
                <div className="flex items-center gap-3 text-sm">
                  <Mail className="w-5 h-5 text-primary shrink-0" />
                  <a href="mailto:contato@equilybracompany.com.br" className="text-primary-foreground/80 hover:text-primary transition-colors">
                    contato@equilybracompany.com.br
                  </a>
                </div>
                <div className="flex items-start gap-3 text-sm">
                  <Clock className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                  <span className="text-primary-foreground/80">
                    Segunda a Sexta: 8h às 21h<br />
                    Sábado: 8h às 16h
                  </span>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <span className="text-sm text-primary-foreground/60">Siga-nos:</span>
                <div className="flex items-center gap-2">
                  <a href="https://www.instagram.com/equilybrainsta/" target="_blank" rel="noopener noreferrer" className="p-2 rounded-lg bg-primary/20 hover:bg-primary/30 transition-colors" aria-label="Instagram">
                    <Instagram className="w-4 h-4" />
                  </a>
                  <a href="https://br.linkedin.com/company/equilybra-clinica-de-psicologia" target="_blank" rel="noopener noreferrer" className="p-2 rounded-lg bg-primary/20 hover:bg-primary/30 transition-colors" aria-label="LinkedIn">
                    <Linkedin className="w-4 h-4" />
                  </a>
                  <a href="https://www.youtube.com/c/equilybrapsicologia" target="_blank" rel="noopener noreferrer" className="p-2 rounded-lg bg-primary/20 hover:bg-primary/30 transition-colors" aria-label="YouTube">
                    <Youtube className="w-4 h-4" />
                  </a>
                </div>
              </div>
            </div>

            {/* Column 2 - Social & Quick Links */}
            <div className="space-y-6">
              <h2 className="text-lg font-display font-semibold">Conecte-se conosco</h2>
              
              <div className="grid grid-cols-3 sm:grid-cols-5 gap-3">
                <a 
                  href="https://www.youtube.com/c/equilybrapsicologia" 
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex flex-col items-center gap-2 p-3 rounded-xl bg-primary/10 hover:bg-primary/20 transition-colors text-center"
                  aria-label="YouTube"
                >
                  <Youtube className="w-7 h-7 text-primary" />
                  <span className="text-xs text-primary-foreground/80">YouTube</span>
                </a>
                <a 
                  href="https://www.instagram.com/equilybrainsta/" 
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex flex-col items-center gap-2 p-3 rounded-xl bg-primary/10 hover:bg-primary/20 transition-colors text-center"
                  aria-label="Instagram"
                >
                  <Instagram className="w-7 h-7 text-primary" />
                  <span className="text-xs text-primary-foreground/80">Instagram</span>
                </a>
                <a 
                  href="https://wa.me/5511976974905" 
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex flex-col items-center gap-2 p-3 rounded-xl bg-[hsl(142_70%_45%)]/20 hover:bg-[hsl(142_70%_45%)]/30 transition-colors text-center"
                  aria-label="WhatsApp"
                >
                  <MessageCircle className="w-7 h-7 text-[hsl(142_70%_50%)]" />
                  <span className="text-xs text-primary-foreground/80">WhatsApp</span>
                </a>
                <a 
                  href="mailto:contato@equilybracompany.com.br"
                  className="flex flex-col items-center gap-2 p-3 rounded-xl bg-primary/10 hover:bg-primary/20 transition-colors text-center"
                  aria-label="Email"
                >
                  <Mail className="w-7 h-7 text-primary" />
                  <span className="text-xs text-primary-foreground/80">Email</span>
                </a>
                <a 
                  href="tel:1123050747"
                  className="flex flex-col items-center gap-2 p-3 rounded-xl bg-primary/10 hover:bg-primary/20 transition-colors text-center"
                  aria-label="Telefone"
                >
                  <Phone className="w-7 h-7 text-primary" />
                  <span className="text-xs text-primary-foreground/80">(11) 2305-0747</span>
                </a>
              </div>

              <div className="pt-4">
                <h3 className="text-sm font-semibold mb-3 text-primary-foreground/90">Links Rápidos</h3>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <Link to="/" className="text-primary-foreground/60 hover:text-primary transition-colors">Home</Link>
                  <Link to="/sobre" className="text-primary-foreground/60 hover:text-primary transition-colors">Sobre</Link>
                  <Link to="/servicos/empresas" className="text-primary-foreground/60 hover:text-primary transition-colors">Para Empresas</Link>
                  <Link to="/contato" className="text-primary-foreground/60 hover:text-primary transition-colors">Contato</Link>
                  <Link to="/servicos/avaliacoes-laudos" className="text-primary-foreground/60 hover:text-primary transition-colors">Avaliações</Link>
                  <a href="https://equilybrapsicologia.blogspot.com/" target="_blank" rel="noopener noreferrer" className="text-primary-foreground/60 hover:text-primary transition-colors">Blog</a>
                </div>
              </div>

              <div className="pt-2">
                <a
                  href="https://transparencyreport.google.com/safe-browsing/search?url=equilybracompany.com.br&hl=pt_BR"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Verificar segurança do site no Google Safe Browsing"
                  className="group relative inline-flex items-center gap-3 rounded-2xl border border-[hsl(142_70%_45%)]/30 bg-gradient-to-r from-[hsl(142_70%_45%)]/15 via-primary/10 to-[hsl(175_50%_55%)]/15 px-5 py-3 backdrop-blur-sm shadow-soft hover:shadow-glow transition-all duration-300 hover:-translate-y-0.5 overflow-hidden"
                >
                  <span className="absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/10 to-transparent group-hover:translate-x-full transition-transform duration-1000" />
                  <span className="relative flex items-center justify-center w-10 h-10 rounded-xl bg-[hsl(142_70%_45%)]/20 ring-1 ring-[hsl(142_70%_45%)]/40">
                    <ShieldCheck className="w-5 h-5 text-[hsl(142_70%_60%)]" strokeWidth={2.2} />
                    <span className="absolute inset-0 rounded-xl animate-pulse-slow bg-[hsl(142_70%_45%)]/10" />
                  </span>
                  <span className="relative flex flex-col leading-tight">
                    <span className="text-[10px] uppercase tracking-widest text-primary-foreground/60">Verificado</span>
                    <span className="text-sm font-semibold text-primary-foreground">Site Seguro</span>
                    <span className="text-[10px] text-primary-foreground/85">Google Safe Browsing</span>
                  </span>
                  <ExternalLink className="relative w-4 h-4 text-primary-foreground/80 group-hover:text-primary transition-colors" />
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Copyright */}
      <div className="relative border-t border-primary-foreground/10 py-6 overflow-hidden">
        <canvas
          ref={canvasRef}
          className="absolute inset-0 w-full h-full pointer-events-none opacity-70"
          aria-hidden="true"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-foreground/40 via-transparent to-foreground/40 pointer-events-none" />
        <div className="container mx-auto px-4 text-center text-sm text-primary-foreground/70 space-y-2 relative z-10">
          <p>© 2026 Equilybra Company. Todos os direitos reservados.</p>
          <p>
            <a href="/politica-de-privacidade" className="hover:text-primary transition-colors underline-offset-4 hover:underline">
              Política de Privacidade (LGPD)
            </a>
          </p>
          <p className="flex items-center justify-center gap-1">
            Desenvolvido por{" "}
            <a 
              href="https://www.topodasbuscas.com.br" 
              target="_blank" 
              rel="noopener noreferrer" 
              className="inline-flex items-center gap-1 hover:text-primary transition-colors"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.3-4.3"/></svg>
              Topo das Buscas
            </a>
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
